import { useCallback, useEffect, useState } from 'react';
import {
  Box, Grid, Paper, Typography, Button,
  List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  pageVariants, sectionVariants, staggerContainer,
} from '../../../animations/variants';
import { getCapabilities, getConfigStatus } from '../../../services/securityhubApi';
import { engineById } from './engines/registry';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading, PortalEmptyState, PortalErrorState, PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import {
  CK, FONT_BODY, sectionCardSx, statTileSx, statTileFooterSx, insetPanelSx,
  sectionHeadingSx, sectionGapSx, splitRowSx, tileEntranceStyle
} from '../portalStyles';

// EnableCapabilities mirrors AWS's "Enable Security Hub → Security capabilities"
// panel: one card per detection engine with its capability summary and an
// Enable/Configure CTA that routes into the engine's own flow.
//
// `embedded` renders the same panel inside another page's card (the Configuration
// page's Capabilities tab): the page header collapses to an in-card section
// header and the page-level entrance animation collapses to a per-card stagger,
// so switching tabs no longer replays a full page transition.
export default function EnableCapabilities({ embedded = false }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCapabilities();
      setRows(res?.data || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const [cfgSummary, setCfgSummary] = useState({ recording: 0, total: 0 });
  useEffect(() => {
    getConfigStatus().then((res) => {
      const recs = res?.data?.recorders || [];
      setCfgSummary({
        recording: recs.filter((r) => r.status === 'enabled').length,
        total: recs.length,
      });
    }).catch(() => {});
  }, []);

  // First engine descriptor (for the "Enable all" forward-looking CTA).
  const firstEngine = rows.length ? engineById(rows[0].engine) : null;

  const enableAllAction = firstEngine && (
    <Button
      variant="outlined"
      onClick={() => navigate(firstEngine.enableRoute)}
    >
      Enable all capabilities
    </Button>
  );

  return (
    <motion.div
      variants={embedded ? staggerContainer : pageVariants}
      initial="initial"
      animate="animate"
    >
      <motion.div variants={sectionVariants}>
        {embedded ? (
          <PortalSectionHeader
            icon={<ToggleOnIcon />}
            title="Security capabilities"
            subtitle="Choose the security capabilities to turn on"
            action={enableAllAction}
          />
        ) : (
          <PortalPageHeader
            icon={<ToggleOnIcon sx={{ fontSize: 24 }} />}
            title="Enable Security Hub"
            subtitle="Choose the security capabilities to turn on"
            action={enableAllAction}
          />
        )}
      </motion.div>

      {error && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={() => load()} />
        </motion.div>
      )}

      {loading && rows.length === 0 ? (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading security capabilities…" />
          </Paper>
        </motion.div>
      ) : (
        // No motion wrapper around the tiles: the tiles animate themselves in
        // pure CSS, and any motion component here can write an inline opacity
        // onto them once its own animation settles.
        <Grid container spacing={3}>
            {rows.map((row, i) => {
              const engine = engineById(row.engine);
              const cap = row.capability || {};
              const color = engine?.color || '#4698D3';
              const Icon = engine?.Icon;
              // Breakpoints must stay identical to the Overview tile grid — both
              // render one tile per engine, and five engines wrap to a 4 + 1
              // orphan row at md: 3. 6-up fits all five on one row with one
              // empty slot. AWS Config is NOT in this grid: it is the separate
              // "Foundational" panel below, outside the container, because it is
              // a capability without a registered engine.
              return (
                <Grid size={{ xs: 12, sm: 6, md: 2 }} key={row.engine}>
                  <div style={tileEntranceStyle(i)}>
                    <Paper elevation={0} sx={statTileSx}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '10px',
                            backgroundColor: `${color}12`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color,
                            flexShrink: 0,
                          }}
                        >
                          {Icon && <Icon sx={{ fontSize: 28 }} />}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={sectionHeadingSx}>
                            {cap.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: FONT_BODY,
                              fontSize: '0.8rem',
                              color: CK.muted,
                              fontWeight: 500,
                            }}
                          >
                            {cap.source}
                          </Typography>
                        </Box>
                      </Box>

                      {cap.description && (
                        <Typography
                          sx={{
                            fontFamily: FONT_BODY,
                            fontSize: '0.875rem',
                            color: CK.body,
                            mb: 1,
                          }}
                        >
                          {cap.description}
                        </Typography>
                      )}

                      {Array.isArray(cap.items) && cap.items.length > 0 && (
                        <List dense disablePadding sx={{ mb: 1 }}>
                          {cap.items.map((item) => (
                            <ListItem key={item} disableGutters sx={{ py: 0 }}>
                              <ListItemIcon sx={{ minWidth: 22, color }}>
                                <FiberManualRecordIcon sx={{ fontSize: 8 }} />
                              </ListItemIcon>
                              <ListItemText
                                primary={item}
                                primaryTypographyProps={{
                                  fontFamily: FONT_BODY,
                                  fontSize: '0.825rem',
                                  color: CK.navy,
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}

                      {/* Pinned footer: every card's CTA lands on one baseline
                          however long its description and item list are. */}
                      <Box
                        sx={{
                          ...statTileFooterSx,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          flexWrap: 'wrap',
                          gap: 1,
                        }}
                      >
                        {row.enabled && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 'auto' }}>
                            <CheckCircleIcon sx={{ color: CK.green, fontSize: 18 }} />
                            <Typography
                              sx={{
                                fontFamily: FONT_BODY,
                                fontSize: '0.8rem',
                                color: CK.green,
                                fontWeight: 600,
                              }}
                            >
                              Enabled
                            </Typography>
                          </Box>
                        )}
                        {/* A capability the backend says it cannot offer here
                            keeps its card and loses its CTA, replaced by the
                            backend's own reason. Hiding the card outright would
                            make the capability read as missing from Prism rather
                            than as unavailable on this installation.

                            `available === false`, never `!available`: the field
                            is absent on any response from a backend predating
                            it, and `!undefined` would strip the CTA off every
                            card on the page. */}
                        {row.available === false ? (
                          <Typography
                            sx={{
                              fontFamily: FONT_BODY,
                              fontSize: '0.8125rem',
                              color: CK.muted,
                              // Own the whole footer row so a long reason reads
                              // left-aligned rather than ragged against the
                              // right edge where the button used to sit.
                              flex: '1 1 100%',
                            }}
                          >
                            {/* The reason is the backend's words, verbatim. The
                                fallback covers only a malformed response — an
                                unavailable card with no CTA and no explanation
                                is the one outcome worse than either. */}
                            {row.unavailableReason || 'This capability is not available on this installation.'}
                          </Typography>
                        ) : (
                          <Button
                            variant={row.enabled ? 'outlined' : 'contained'}
                            disabled={!engine}
                            onClick={() => engine && navigate(engine.enableRoute)}
                          >
                            {row.enabled ? 'Configure' : 'Enable'}
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  </div>
                </Grid>
              );
            })}
        </Grid>
      )}

      <motion.div variants={sectionVariants}>
        <Box sx={sectionGapSx}>
          <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Foundational</Typography>
          <Box sx={{ ...insetPanelSx, ...splitRowSx, p: 3 }}>
            <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy }}>
                AWS Config (recording)
              </Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body }}>
                Records resource configuration so Security Hub standards can evaluate.
                {' '}{cfgSummary.recording}/{cfgSummary.total} cells recording.
              </Typography>
            </Box>
            <Button variant="outlined" onClick={() => navigate('/securityhub/config')}>Manage</Button>
          </Box>
        </Box>
      </motion.div>

      {!loading && !error && rows.length === 0 && (
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={{ ...sectionCardSx, ...sectionGapSx }}>
            <PortalEmptyState
              icon={<ToggleOnIcon sx={{ fontSize: 28 }} />}
              title="No capabilities available"
              description="No security capabilities are available for this tenant. Contact your CloudKeeper administrator to have them enabled."
            />
          </Paper>
        </motion.div>
      )}
    </motion.div>
  );
}
